import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import WhiteButtonComponent from "../../components/shared/buttons/white-button-component";
import BlackButtonComponent from "../../components/shared/buttons/black-button-component";
import TripInfoForm from "./components/TripInfoForm";
import TripSections from "./components/TripSections";
import TripFaqs from "./components/TripFaqs";
import useTripForm from "./formik/useTripForm";
import { useEffect, useState } from "react";
import { useAppSelector } from "store";
import { toast } from "react-toastify";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { ImageModal } from "components/index";
import { TripImage } from "assets/index";
import Inclusion from "./components/Inclusion";
import Exclusion from "./components/Exclusion";
import TTCarryList from "./components/ThingToCarry";

const CreateNewTripPage = () => {
  const { formik } = useTripForm();
  const [showExampleImage, setShowExampleImage] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const { userId } = useAppSelector(
    (state) => state.appLayout.userDetails
  ) as any;

  const handleSaveDraft = () => {
    const formValues = formik.values;

    // Check if at least one or two fields are filled
    const isFormPartiallyFilled =
      Object.values(formValues).filter((value) => !!value).length >= 2;

    if (!isFormPartiallyFilled) {
      toast.error("Please fill at least few fields before saving the draft.");
      return;
    }

    // Save draft data with merchantId
    const draftData = {
      ...formValues,
    };

    localStorage.setItem("tripDraft", JSON.stringify(draftData));
    toast.success("Draft saved successfully!");
  };

  useEffect(() => {
    const savedDraft = localStorage.getItem("tripDraft");

    if (savedDraft) {
      const draftData = JSON.parse(savedDraft);
      if (draftData.merchantId === userId) {
        formik.setValues(draftData);
        localStorage.removeItem("tripDraft");
      } else {
        console.log("Draft does not belong to the current user.");
      }
    }
  }, []);

  const handleSubmit = async () => {
    const errors = await formik.validateForm();
  
    if (Object.keys(errors).length > 0) {
      // Get only the first error to display
      const firstErrorField = Object.keys(errors)[0];
      const errorMessage = `${firstErrorField.charAt(0).toUpperCase() + firstErrorField.slice(1)} is incomplete`;
      
      toast.error(errorMessage);
      return;
    }
  
    formik.handleSubmit();
  };

  return (
    <Box padding={{ xs: 1, sm: 2, md: 3 }}>
      {/* Header Section */}
      <Box justifyContent="space-between" gap={2}>
        <Box
          display="flex"
          flexDirection={isMobile ? "column" : "row"}
          alignItems={isMobile ? "flex-start" : "center"}
          gap={isMobile ? 2 : 0}
        >
          <Box flex={1}>
            <Typography
              variant={isMobile ? "h6" : "h5"}
              fontWeight="600"
              color="#181D27"
            >
              Create Trip{" "}
            </Typography>
            <Typography
              variant={isMobile ? "body2" : "subtitle1"}
              color="#535862"
              fontWeight="400"
              sx={{ mt: isMobile ? 0.5 : 0 }}
            >
              (To add images and slots to the created trip please edit the trip
              from Trips Created.)
            </Typography>
          </Box>

          {/* example of new created trip image */}
          <WhiteButtonComponent
            btnText="See Example"
            onClick={() => setShowExampleImage(true)}
            sx={{
              cursor: "pointer",
              marginLeft: isMobile ? 0 : "auto",
              minWidth: isMobile ? "100%" : "auto",
            }}
          />
        </Box>

        <Box
          sx={{
            mt: 1,
            p: { xs: 1.5, sm: 2 },
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            mb: 3,
          }}
        >
          <Box
            component="ul"
            sx={{ pl: { xs: 2, sm: 3 }, mb: 3, "& li": { mb: 1.5 } }}
          >
            <Typography
              variant={isMobile ? "body2" : "body1"}
              sx={{ my: { xs: 1, sm: 2 }, textAlign: "flex-start" }}
            >
              Once you've created a trip, you can enhance it further by adding
              images and slots through the Trips Created page. Simply locate the
              specific trip and update it as needed, from the images/slot
              pending status.
            </Typography>

            <Box display="flex" alignItems="flex-start" gap={1} sx={{ mb: 1 }}>
              <CheckCircleOutlineIcon
                color="success"
                fontSize={isMobile ? "small" : "medium"}
                sx={{ mt: 0.3 }}
              />
              <Typography variant={isMobile ? "body2" : "body1"}>
                <strong>Images</strong>: Upload high-quality visuals to showcase
                the experience.
              </Typography>
            </Box>

            <Box display="flex" alignItems="flex-start" gap={1} sx={{ mb: 1 }}>
              <CheckCircleOutlineIcon
                color="success"
                fontSize={isMobile ? "small" : "medium"}
                sx={{ mt: 0.3 }}
              />
              <Typography variant={isMobile ? "body2" : "body1"}>
                <strong>Slots</strong>: Enable bookings for similar trips with
                different start & end dates, locations, or hosts while keeping
                the core experience intact.
              </Typography>
            </Box>

            <Typography
              variant={isMobile ? "body2" : "body1"}
              sx={{ my: { xs: 1, sm: 2 }, textAlign: "flex-start" }}
            >
              This feature gives you the flexibility to manage multiple
              variations of a trip effortlessly, ensuring a seamless booking
              experience for travellers.
            </Typography>
          </Box>

          <Typography
            variant={isMobile ? "body2" : "body1"}
            sx={{
              mt: 2,
              p: { xs: 1.5, sm: 2 },
              borderRadius: 1,
              bgcolor: "warning.light",
              display: "flex",
              gap: 1,
              alignItems: isMobile ? "flex-start" : "center",
              flexDirection: isMobile ? "column" : "row",
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <WarningAmberRoundedIcon
                color="warning"
                fontSize={isMobile ? "small" : "medium"}
              />
              <Box component="span" fontWeight="600">
                Prohibited:
              </Box>
            </Box>
            <Box>
              Sharing contact details, social media links, or direct
              communication methods may lead to account suspension.
            </Box>
          </Typography>
        </Box>
      </Box>

      {/* Trip Information */}
      <TripInfoForm formik={formik} />
      <Inclusion formik={formik} />
      <TripSections formik={formik} />
      <Exclusion formik={formik} />
      <TTCarryList formik={formik} />
      {/* Sections & FAQs */}
      <TripFaqs formik={formik} />

      {/* Action Buttons */}
      <Box
        display="flex"
        flexDirection={isTablet ? "column" : "row"}
        justifyContent="space-between"
        marginTop={5}
        gap={2}
      >
        <WhiteButtonComponent
          sx={{ width: isTablet ? "100%" : "250px", height: "44px" }}
          btnText="Save draft"
          onClick={handleSaveDraft}
        />
        <BlackButtonComponent
          sx={{ width: isTablet ? "100%" : "250px", height: "44px" }}
          onClick={handleSubmit}
          btnText={formik.isSubmitting ? "Creating..." : "Create Trip"}
          disabled={formik.isSubmitting}
        />
      </Box>
      <ImageModal
        open={showExampleImage}
        onClose={() => setShowExampleImage(false)}
      >
        <img
          src={TripImage}
          alt="Trip example"
          style={{
            maxWidth: "100%",
            height: "auto",
            borderRadius: 8,
          }}
        />
      </ImageModal>
    </Box>
  );
};

export default CreateNewTripPage;
